import Cookies from 'universal-cookie';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState, createContext } from 'react';
import { authLog, authTrail, fingerprint } from 'src/sentry';

import { postToApp } from 'src/utils/post-to-app';

export interface AuthContextValue {
  auth: string | null | undefined;
  setAuth: (token?: string | null) => void;
  /** Last token cleared via setAuth(null) — never re-adopted from native storage. */
  clearedToken: React.MutableRefObject<string | null | undefined>;
}

const AuthContext = createContext<AuthContextValue>({} as AuthContextValue);

type Props = {
  children?: React.ReactNode;
};

export const AuthProvider = ({ children }: Props) => {
  const cookies = new Cookies();
  const defaultAuth = cookies.get('megi-authorization');

  const [auth, setAuth] = useState<string | null | undefined>(defaultAuth);
  const clearedToken = useRef<string | null | undefined>(null);
  const queryClient = useQueryClient();

  const handleSetAuth = (token?: string | null) => {
    if (!token) {
      // token clear — previous token fingerprint, route, and native webview presence
      authLog('auth:clear', {
        prevTokenFp: fingerprint(auth),
        route: window.location.pathname,
        nativeWebview: !!window.ReactNativeWebView,
      });
      clearedToken.current = auth;
      cookies.remove('megi-authorization', { path: '/' });
      postToApp('SET_AUTH_TOKEN', { token: null });
      // Drop every cached query (patient readings, messages, case studies…) so none of the
      // previous session's data outlives it in memory.
      queryClient.clear();
      setAuth(null);
      return;
    }
    // token set — fingerprint + route to catch cookie/native secure-store drift
    authTrail('auth:set', { tokenFp: fingerprint(token), route: window.location.pathname });
    postToApp('SET_AUTH_TOKEN', { token });
    // set age to 1 year
    cookies.set('megi-authorization', token, { path: '/', maxAge: 31556952 });
    setAuth(token);
  };

  return <AuthContext.Provider value={{ auth, setAuth: handleSetAuth, clearedToken }}>{children}</AuthContext.Provider>;
};

export default AuthContext;
